import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Row } from 'antd-v5';
import LanguageSelect from 'components/LanguageSelect';
import {
  changeDocumentLanguageSaga,
  changeWebLanguageSaga,
  getDocumentLanguage,
  getDocumentLanguageFallback,
  getWebLanguage,
  updateUserProfileLanguagesRequestSaga,
} from 'domains/user';
import { Box, Flex, Text } from 'UI';
import { notifyTop } from 'UI/Notification/notification';
import { Language } from 'utils/i18n/Language';
import { SLanguageSelectPosition } from './LanguageSettings.styled';

const LanguageSettings = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const webLang = useSelector(getWebLanguage);
  const docLangPrimary = useSelector(getDocumentLanguage);
  const docLangSecondary = useSelector(getDocumentLanguageFallback);

  const availableWebLanguages = Language.listWebLanguages();
  const availableDocLanguages = Language.listDocumentLanguages();

  const handleSelectedWebLang = (selectedWebLang: Language) => {
    if (!Language.languagesAreEqual(webLang, selectedWebLang)) {
      dispatch(changeWebLanguageSaga(selectedWebLang));
      saveUserProfileLanguages(selectedWebLang, docLangPrimary, docLangSecondary);
    }
  };

  const handleSelectedDocLang = (selectedDocLang: Language) => {
    if (!Language.languagesAreEqual(docLangPrimary, selectedDocLang)) {
      dispatch(
        changeDocumentLanguageSaga({
          documentLanguage: selectedDocLang,
          documentLanguageFallback: docLangSecondary,
        }),
      );
      saveUserProfileLanguages(webLang, selectedDocLang, docLangSecondary);
    }
  };

  const handleSelectedDocSecondaryLang = (selectedDocSecondaryLang: Language) => {
    if (!Language.languagesAreEqual(docLangSecondary, selectedDocSecondaryLang)) {
      dispatch(
        changeDocumentLanguageSaga({
          documentLanguage: docLangPrimary,
          documentLanguageFallback: selectedDocSecondaryLang,
        }),
      );
    }
    saveUserProfileLanguages(webLang, docLangPrimary, selectedDocSecondaryLang);
  };

  const saveUserProfileLanguages = (
    webLanguage: Language,
    documentLanguage: Language,
    documentLanguageFallback: Language,
  ) => {
    dispatch(
      updateUserProfileLanguagesRequestSaga({
        webLanguage,
        documentLanguage,
        documentLanguageFallback,
      }),
    );
    notifyTop('success', 'Updated successfully', '');
  };

  return (
    <Flex direction={'column'} gap={25} data-cy={'language-settings-section'}>
      <Text type="heading4">{t('settings.general_settings.language_settings.title', 'Language')}</Text>
      <Flex justify={'flex-start'} align={'flex-start'} direction={'row'} wrap={'wrap'}>
        <Box height={100} width={300}>
          <Row justify={'start'}>
            <Text type="lg">{t('settings.general_settings.language.website', 'Website Language')}</Text>
          </Row>
          <SLanguageSelectPosition data-cy={'website-language-dropdown'}>
            <LanguageSelect languageList={availableWebLanguages} selected={webLang} onSelect={handleSelectedWebLang} />
          </SLanguageSelectPosition>
        </Box>
        <Box height={100} width={300}>
          <Row justify={'start'}>
            <Text type="lg">{t('settings.general_settings.language.documentation', 'Documentation Language')}</Text>
          </Row>
          <SLanguageSelectPosition data-cy={'documentation-language-dropdown'}>
            <LanguageSelect
              languageList={availableDocLanguages}
              selected={docLangPrimary}
              onSelect={handleSelectedDocLang}
            />
          </SLanguageSelectPosition>
        </Box>
        <Box height={100} width={300}>
          <Row justify={'start'}>
            <Flex direction={'column'}>
              <Text type="lg">
                {t('settings.general_settings.language.secondary_documentation', 'Secondary Documentation Language')}
              </Text>
            </Flex>
          </Row>
          <SLanguageSelectPosition data-cy={'secondary-documentation-language-dropdown'}>
            <LanguageSelect
              languageList={availableDocLanguages}
              selected={docLangSecondary}
              onSelect={handleSelectedDocSecondaryLang}
            />
          </SLanguageSelectPosition>
        </Box>
      </Flex>
    </Flex>
  );
};

export default LanguageSettings;
